import React, { useState, useEffect, useCallback } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { jsPDF } from "jspdf";
import { Search, Download, AlertTriangle, Info, BarChart2, PieChart, Activity, ChevronDown, ChevronUp, Filter, Database } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChakraProvider, Box, VStack, HStack, Input, Textarea, Button, Text, Heading, Grid, GridItem, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Tabs, TabList, TabPanels, Tab, TabPanel, Badge, Tooltip as ChakraTooltip, useColorModeValue, CircularProgress, CircularProgressLabel, Collapse, IconButton, Select, Checkbox, Flex } from "@chakra-ui/react";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useSpring, animated } from 'react-spring';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend);

// Initialize Gemini AI
const genAI = new GoogleGenerativeAI('AIzaSyDDHyTigKNL81wqgpU2VkjZR-innDbq8Cw');
const model = genAI.getGenerativeModel({ model: "gemini-1.5-pro" });

// Local database of Indian legal cases
const localCaseDatabase = [
  {
  title: "State of Tamil Nadu v. Arjun Verma",
  date: "2021-07-12",
  charges: "Attempted Murder (Section 307 IPC)",
  description: "The accused was charged with the attempted murder of his neighbor after a heated altercation over property disputes. The defense claimed diminished responsibility due to a psychiatric condition.",
  verdict: "Convicted and sentenced to 10 years imprisonment",
  similarities: "Personal dispute, violent altercation",
  differences: "Non-fatal outcome, mental health defense",
  similarityScore: 65,
  precedentValue: 60,
  legalImpact: 55,
  publicInterest: 45
},
{
  title: "Ramesh Singh v. State of Haryana",
  date: "2022-11-22",
  charges: "Kidnapping (Section 363 IPC)",
  description: "The accused was charged with kidnapping a minor for ransom. The prosecution presented evidence of surveillance footage and phone records linking the accused to the crime.",
  verdict: "Convicted, sentenced to 15 years imprisonment",
  similarities: "Involvement of a minor, ransom demand",
  differences: "No physical harm to the victim, quick police rescue",
  similarityScore: 80,
  precedentValue: 75,
  legalImpact: 70,
  publicInterest: 85
},
{
  title: "Anita Rao v. Ravi Kapoor",
  date: "2023-02-14",
  charges: "Defamation (Section 500 IPC)",
  description: "The plaintiff, a businesswoman, accused the defendant, a rival entrepreneur, of spreading false claims about her business practices on social media.",
  verdict: "Judgment in favor of the plaintiff, damages awarded",
  similarities: "Business rivalry, public defamation",
  differences: "Social media involvement, no physical harm",
  similarityScore: 70,
  precedentValue: 80,
  legalImpact: 60,
  publicInterest: 50
},
{
  title: "State of Punjab v. Surinder Singh",
  date: "2022-05-18",
  charges: "Robbery (Section 392 IPC)",
  description: "The accused was charged with robbing a local bank at gunpoint. The defense argued that the accused was coerced into the crime by a gang threatening his family.",
  verdict: "Convicted, sentenced to 12 years imprisonment",
  similarities: "Armed robbery, financial motive",
  differences: "Coercion defense, no casualties",
  similarityScore: 75,
  precedentValue: 70,
  legalImpact: 65,
  publicInterest: 75
},
{
  title: "Meera Bansal v. Sunil Agarwal",
  date: "2023-07-25",
  charges: "Breach of Contract (Contract Act, 1872)",
  description: "The plaintiff sued the defendant for failing to deliver on a construction contract, causing significant financial losses and project delays.",
  verdict: "Judgment in favor of the plaintiff, compensation awarded",
  similarities: "Commercial contract, financial loss",
  differences: "Construction industry, civil case",
  similarityScore: 60,
  precedentValue: 85,
  legalImpact: 70,
  publicInterest: 55
},
{
  title: "State of Rajasthan v. Vikram Yadav",
  date: "2021-12-03",
  charges: "Drug Trafficking (NDPS Act, 1985)",
  description: "The accused was caught with a large quantity of illegal narcotics during a police raid. The defense claimed entrapment by law enforcement.",
  verdict: "Convicted and sentenced to 20 years imprisonment",
  similarities: "Illegal narcotics, large-scale operation",
  differences: "Claim of entrapment, cross-border operation",
  similarityScore: 85,
  precedentValue: 90,
  legalImpact: 80,
  publicInterest: 95
},
{
  title: "Deepak Mehta v. M/s XYZ Financial Services",
  date: "2023-03-30",
  charges: "Fraud (Section 420 IPC)",
  description: "The plaintiff accused the financial services company of defrauding him through a Ponzi scheme, causing significant financial losses to multiple investors.",
  verdict: "Judgment in favor of the plaintiff, compensation awarded",
  similarities: "Financial fraud, multiple victims",
  differences: "Corporate entity, Ponzi scheme",
  similarityScore: 70,
  precedentValue: 75,
  legalImpact: 85,
  publicInterest: 90
},
{
  title: "State of Kerala v. Rajiv Menon",
  date: "2022-08-19",
  charges: "Human Trafficking (Section 370 IPC)",
  description: "The accused was charged with trafficking women across state borders for forced labor. The case drew attention for its links to organized crime.",
  verdict: "Convicted and sentenced to life imprisonment",
  similarities: "Human trafficking, organized crime",
  differences: "Cross-border trafficking, forced labor",
  similarityScore: 90,
  precedentValue: 85,
  legalImpact: 95,
  publicInterest: 100
},
{
  title: "State of Uttar Pradesh v. Akash Chauhan",
  date: "2022-04-15",
  charges: "Assault (Section 351 IPC)",
  description: "The accused was charged with assaulting a co-worker after a heated argument at a construction site.",
  verdict: "Convicted and sentenced to 2 years imprisonment",
  similarities: "Workplace violence, physical altercation",
  differences: "Minor injuries, no prior history",
  similarityScore: 50,
  precedentValue: 45,
  legalImpact: 40,
  publicInterest: 35
},
{
  title: "Seema Kapoor v. Ajay Enterprises",
  date: "2023-06-05",
  charges: "Breach of Contract (Contract Act, 1872)",
  description: "The plaintiff sued the defendant for not delivering promised services in a wedding planning contract.",
  verdict: "Judgment in favor of the plaintiff, partial compensation awarded",
  similarities: "Service contract, financial loss",
  differences: "Wedding industry, emotional distress",
  similarityScore: 55,
  precedentValue: 60,
  legalImpact: 50,
  publicInterest: 40
},
{
  title: "State of West Bengal v. Gaurav Sen",
  date: "2022-09-25",
  charges: "Domestic Violence (Section 498A IPC)",
  description: "The accused was charged with domestic violence after repeated abuse towards his spouse was reported by neighbors.",
  verdict: "Convicted and sentenced to 5 years imprisonment",
  similarities: "Spousal abuse, physical and emotional harm",
  differences: "Multiple incidents, victim testimony",
  similarityScore: 80,
  precedentValue: 85,
  legalImpact: 70,
  publicInterest: 90
},
{
  title: "Manoj Jain v. Gopal Industries",
  date: "2021-12-10",
  charges: "Product Liability (Consumer Protection Act, 2019)",
  description: "The plaintiff sued the defendant for selling defective machinery that caused injuries during use.",
  verdict: "Judgment in favor of the plaintiff, damages awarded",
  similarities: "Defective product, personal injury",
  differences: "Machinery, industrial setting",
  similarityScore: 65,
  precedentValue: 75,
  legalImpact: 60,
  publicInterest: 50
},
{
  title: "State of Assam v. Rohit Dutta",
  date: "2023-03-08",
  charges: "Cybercrime (Information Technology Act, 2000)",
  description: "The accused was charged with hacking into government systems and leaking sensitive data online.",
  verdict: "Convicted and sentenced to 7 years imprisonment",
  similarities: "Cybercrime, government systems",
  differences: "Leaked data, national security",
  similarityScore: 85,
  precedentValue: 90,
  legalImpact: 85,
  publicInterest: 95
},
{
  title: "Maya Verma v. Rajesh Patil",
  date: "2022-11-15",
  charges: "Trespassing (Section 441 IPC)",
  description: "The plaintiff accused the defendant of illegally entering her property and damaging crops.",
  verdict: "Judgment in favor of the plaintiff, fine imposed",
  similarities: "Property dispute, illegal entry",
  differences: "Agricultural land, no physical harm",
  similarityScore: 60,
  precedentValue: 55,
  legalImpact: 50,
  publicInterest: 40
},
{
  title: "State of Bihar v. Rajan Kumar",
  date: "2022-02-28",
  charges: "Illegal Mining (Mines and Minerals Act, 1957)",
  description: "The accused was charged with conducting illegal mining operations in a protected forest area.",
  verdict: "Convicted and sentenced to 8 years imprisonment",
  similarities: "Environmental crime, resource extraction",
  differences: "Protected forest, large-scale operation",
  similarityScore: 70,
  precedentValue: 75,
  legalImpact: 80,
  publicInterest: 85
},
{
  title: "Ashok Mehta v. Prakash Builders",
  date: "2023-04-01",
  charges: "Fraud (Section 420 IPC)",
  description: "The plaintiff accused the defendant of defrauding him by selling non-existent plots of land.",
  verdict: "Judgment in favor of the plaintiff, compensation awarded",
  similarities: "Real estate fraud, financial loss",
  differences: "Non-existent property, civil fraud",
  similarityScore: 65,
  precedentValue: 70,
  legalImpact: 60,
  publicInterest: 55
},
{
  title: "State of Telangana v. Anil Rao",
  date: "2023-01-10",
  charges: "Kidnapping (Section 363 IPC)",
  description: "The accused was charged with kidnapping his estranged daughter after a custody battle with his ex-wife.",
  verdict: "Convicted and sentenced to 12 years imprisonment",
  similarities: "Child kidnapping, family dispute",
  differences: "Custody battle, parental involvement",
  similarityScore: 75,
  precedentValue: 70,
  legalImpact: 65,
  publicInterest: 80
},
{
  title: "Nisha Sharma v. Horizon Tech Ltd.",
  date: "2022-07-18",
  charges: "Sexual Harassment (POSH Act, 2013)",
  description: "The plaintiff, an employee, accused a senior manager of sexual harassment at the workplace.",
  verdict: "Judgment in favor of the plaintiff, strict penalties imposed",
  similarities: "Workplace harassment, sexual misconduct",
  differences: "Corporate environment, gender dynamics",
  similarityScore: 85,
  precedentValue: 90,
  legalImpact: 75,
  publicInterest: 80
},
{
  title: "State of Maharashtra v. Santosh Khedekar",
  date: "2021-08-20",
  charges: "Arson (Section 435 IPC)",
  description: "The accused was charged with setting fire to a local shop after a business rivalry turned violent.",
  verdict: "Convicted and sentenced to 10 years imprisonment",
  similarities: "Business rivalry, property damage",
  differences: "Arson, commercial property",
  similarityScore: 65,
  precedentValue: 55,
  legalImpact: 50,
  publicInterest: 60
},
{
  title: "Manish Pandey v. Reliance Motors",
  date: "2023-05-05",
  charges: "Consumer Fraud (Consumer Protection Act, 2019)",
  description: "The plaintiff sued the defendant for selling a defective car under false pretenses.",
  verdict: "Judgment in favor of the plaintiff, full refund awarded",
  similarities: "Defective product, consumer rights",
  differences: "Automobile industry, civil case",
  similarityScore: 70,
  precedentValue: 65,
  legalImpact: 60,
  publicInterest: 50
},
{
  title: "State of Andhra Pradesh v. Suresh Chandra",
  date: "2022-10-10",
  charges: "Extortion (Section 384 IPC)",
  description: "The accused was charged with extorting money from local shop owners by threatening violence.",
  verdict: "Convicted and sentenced to 7 years imprisonment",
  similarities: "Extortion, financial motive",
  differences: "Small-scale operation, local impact",
  similarityScore: 60,
  precedentValue: 55,
  legalImpact: 50,
  publicInterest: 65
},
{
  title: "Anjali Verma v. PQR Media Ltd.",
  date: "2023-02-28",
  charges: "Libel (Section 499 IPC)",
  description: "The plaintiff accused the defendant media company of publishing false and defamatory articles about her.",
  verdict: "Judgment in favor of the plaintiff, damages awarded",
  similarities: "Defamation, media involvement",
  differences: "Libel, written defamation",
  similarityScore: 75,
  precedentValue: 70,
  legalImpact: 65,
  publicInterest: 55
},
{
  title: "State of Gujarat v. Mahesh Patel",
  date: "2023-08-10",
  charges: "Counterfeiting (Section 489B IPC)",
  description: "The accused was charged with producing and distributing counterfeit currency notes.",
  verdict: "Convicted and sentenced to 15 years imprisonment",
  similarities: "Financial crime, counterfeit money",
  differences: "National security implications, organized crime",
  similarityScore: 85,
  precedentValue: 90,
  legalImpact: 80,
  publicInterest: 95
},
{
  title: "Rohan Das v. Zenon Airlines",
  date: "2022-12-20",
  charges: "Negligence (Torts Law)",
  description: "The plaintiff sued the defendant for negligence after suffering injuries during a turbulent flight due to lack of safety measures.",
  verdict: "Judgment in favor of the plaintiff, compensation awarded",
  similarities: "Negligence, personal injury",
  differences: "Airline industry, tort law",
  similarityScore: 65,
  precedentValue: 70,
  legalImpact: 60,
  publicInterest: 50
},
{
  title: "State of Haryana v. Sameer Kapoor",
  date: "2021-06-15",
  charges: "Illegal Arms Possession (Section 25 Arms Act, 1959)",
  description: "The accused was charged with illegal possession of firearms without a valid license.",
  verdict: "Convicted and sentenced to 6 years imprisonment",
  similarities: "Illegal possession, firearms",
  differences: "No use of firearms, personal collection",
  similarityScore: 55,
  precedentValue: 60,
  legalImpact: 50,
  publicInterest: 65
},
{
  title: "Kavita Singh v. Delhi Transport Corporation",
  date: "2023-03-12",
  charges: "Wrongful Termination (Labour Laws)",
  description: "The plaintiff, a bus driver, accused her employer of wrongful termination after a workplace dispute.",
  verdict: "Judgment in favor of the plaintiff, reinstatement ordered",
  similarities: "Employment dispute, wrongful termination",
  differences: "Public sector employer, gender dynamics",
  similarityScore: 70,
  precedentValue: 75,
  legalImpact: 65,
  publicInterest: 60
},
{
  title: "State of Odisha v. Deepak Sharma",
  date: "2022-05-25",
  charges: "Poaching (Wildlife Protection Act, 1972)",
  description: "The accused was charged with poaching endangered wildlife in a national park for illegal trade.",
  verdict: "Convicted and sentenced to 10 years imprisonment",
  similarities: "Wildlife crime, endangered species",
  differences: "National park, organized poaching ring",
  similarityScore: 80,
  precedentValue: 85,
  legalImpact: 75,
  publicInterest: 90
},
{
  title: "Rajeev Desai v. State Bank of India",
  date: "2022-08-02",
  charges: "Breach of Trust (Section 409 IPC)",
  description: "The plaintiff accused the bank of breaching trust by mishandling his savings account, resulting in financial losses.",
  verdict: "Judgment in favor of the plaintiff, compensation awarded",
  similarities: "Financial loss, institutional defendant",
  differences: "Banking sector, breach of trust",
  similarityScore: 60,
  precedentValue: 65,
  legalImpact: 50,
  publicInterest: 55
},
{
  title: "State of Kerala v. Neha Menon",
  date: "2023-09-10",
  charges: "Forgery (Section 463 IPC)",
  description: "The accused was charged with forging educational documents to obtain a government job.",
  verdict: "Convicted and sentenced to 5 years imprisonment",
  similarities: "Forgery, fraudulent documents",
  differences: "Employment fraud, government sector",
  similarityScore: 70,
  precedentValue: 75,
  legalImpact: 65,
  publicInterest: 60
}
];

const CaseComparisonComponent = () => {
  const [currentCase, setCurrentCase] = useState({
    title: '',
    date: '',
    charges: '',
    description: '',
    verdict: ''
  });
  const [similarCases, setSimilarCases] = useState([]);
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [error, setError] = useState(null);
  const [caseSimilarityData, setCaseSimilarityData] = useState([]);
  const [caseTimeline, setCaseTimeline] = useState([]);
  const [radarData, setRadarData] = useState([]);
  const [isInputExpanded, setIsInputExpanded] = useState(true);
  const [filterCriteria, setFilterCriteria] = useState({
    charges: '',
    minSimilarity: 0,
    onlyHighImpact: false
  });
  const [isUsingLocalData, setIsUsingLocalData] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const handleInputChange = (e) => {
    setCurrentCase({ ...currentCase, [e.target.name]: e.target.value });
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilterCriteria(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const fetchSimilarCases = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!currentCase.title || !currentCase.date || !currentCase.charges || !currentCase.description) {
        throw new Error("Please fill in all required fields.");
      }
  
      let cases;
      if (isUsingLocalData) {
        // Use local database with improved matching
        cases = localCaseDatabase.filter(c => {
          const descriptionMatch = c.description.toLowerCase().includes(currentCase.description.toLowerCase());
          const chargeMatch = filterCriteria.charges === '' || c.charges.toLowerCase().includes(filterCriteria.charges.toLowerCase());
          const similarityMatch = c.similarityScore >= filterCriteria.minSimilarity;
          const impactMatch = !filterCriteria.onlyHighImpact || c.legalImpact > 70;
          
          return descriptionMatch && chargeMatch && similarityMatch && impactMatch;
        });
  
        // Calculate similarity scores based on description match
        cases = cases.map(c => ({
          ...c,
          similarityScore: calculateSimilarity(currentCase.description, c.description)
        }));
  
        // Sort cases by similarity score in descending order
        cases.sort((a, b) => b.similarityScore - a.similarityScore);
      } else {
        // Fetch from API (unchanged)
        const prompt = `Given the following case details:
          Title: ${currentCase.title}
          Description: ${currentCase.description}
  
          Please provide 2 similar historical Indian law cases with the following details for each:
          1. Case title
          2. Date
          3. Charges
          4. Brief description
          5. Verdict
          
  
          Also, provide a brief analysis comparing the given case to these historical cases.`;
  
        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();
        
        console.log('Raw API response:', text);
  
        // Parse the API response (simplified for brevity)
        cases = parseApiResponse(text);
      }
  
      if (cases.length === 0) {
        throw new Error("No similar cases found matching the criteria.");
      }
  
      setSimilarCases(cases);
      setAnalysis(generateAnalysis(cases));
      updateChartData(cases);
  
      toast({
        title: "Cases found",
        description: `Retrieved ${cases.length} similar cases successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      setIsInputExpanded(false);
    } catch (error) {
      console.error('Error fetching similar cases:', error);
      setError(`Error: ${error.message}`);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to calculate similarity between two strings
  const calculateSimilarity = (str1, str2) => {
    const set1 = new Set(str1.toLowerCase().split(' '));
    const set2 = new Set(str2.toLowerCase().split(' '));
    const intersection = new Set([...set1].filter(x => set2.has(x)));
    const union = new Set([...set1, ...set2]);
    return Math.round((intersection.size / union.size) * 100);
  };
  const parseApiResponse = (text) => {
    // Implement more robust parsing logic here
    // This is a simplified version
    const cases = text.split(/Case \d+:/).slice(1).map(caseText => {
      const lines = caseText.split('\n').filter(line => line.trim() !== '');
      const caseData = {};
      lines.forEach(line => {
        const [key, value] = line.split(':').map(s => s.trim());
        caseData[key.toLowerCase()] = value;
      });
      return {
        title: caseData.title,
        date: caseData.date,
        charges: caseData.charges,
        description: caseData.description,
        verdict: caseData.verdict,
        similarities: caseData.similarities,
        differences: caseData.differences,
        similarityScore: parseInt(caseData['similarity score']) || 0,
        precedentValue: parseInt(caseData['precedent value']) || 0,
        legalImpact: parseInt(caseData['legal impact']) || 0,
        publicInterest: parseInt(caseData['public interest']) || 0
      };
    });
    return cases;
  };

  const generateAnalysis = (cases) => {
    // Generate a simple analysis based on the cases
    const avgSimilarity = cases.reduce((sum, c) => sum + c.similarityScore, 0) / cases.length;
    const highestImpactCase = cases.reduce((prev, current) => (prev.legalImpact > current.legalImpact) ? prev : current);
    
    return `Analysis of ${cases.length} similar cases:
    
    1. Average Similarity: The cases show an average similarity score of ${avgSimilarity.toFixed(2)}%.
    2. Highest Impact: The case "${highestImpactCase.title}" has the highest legal impact score of ${highestImpactCase.legalImpact}%.
    3. Key Trends: ${identifyTrends(cases)}
    
    These cases provide valuable insights into how similar matters have been handled in the Indian legal system. The precedents set by these cases, especially those with high legal impact, should be carefully considered when approaching the current case.`;
  };

  const identifyTrends = (cases) => {
    const charges = cases.map(c => c.charges);
    const mostCommonCharge = mode(charges);
    const recentTrend = cases.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    
    return `The most common charge among similar cases is "${mostCommonCharge}". 
    Recent trends, as seen in the case "${recentTrend.title}" (${recentTrend.date}), suggest ${recentTrend.verdict.toLowerCase()}.`;
  };

  const mode = (arr) => {
    return arr.sort((a,b) =>
          arr.filter(v => v===a).length
        - arr.filter(v => v===b).length
    ).pop();
  };

  const updateChartData = (cases) => {
    setCaseSimilarityData(cases.map(c => ({ name: c.title, similarity: c.similarityScore })));
    setCaseTimeline([currentCase, ...cases].map(c => ({ name: c.title, date: c.date })));
    setRadarData(cases.map(c => ({
      subject: c.title,
      'Similarity': c.similarityScore,
      'Precedent Value': c.precedentValue,
      'Legal Impact': c.legalImpact,
      'Public Interest': c.publicInterest
    })));
  };

  const generatePDF = useCallback(() => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Case Comparison Report', 10, 20);
    doc.setFontSize(12);
    doc.text(`Current Case: ${currentCase.title}`, 10, 30);
    doc.text('Similar Cases:', 10, 40);
    similarCases.forEach((c, i) => {
      doc.text(`${i + 1}. ${c.title} (Similarity: ${c.similarityScore}%)`, 10, 50 + i * 10);
    });
    doc.text('Analysis:', 10, 90);
    const splitText = doc.splitTextToSize(analysis, 180);
    doc.text(splitText, 10, 100);
    doc.save('case-comparison-report.pdf');

    toast({
      title: "PDF Generated",
      description: "Your report has been downloaded.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  }, [currentCase, similarCases, analysis, toast]);

  const pieChartData = {
    labels: similarCases.map(c => c.title),
    datasets: [
      {
        data: similarCases.map(c => c.similarityScore),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const AnimatedNumber = ({ n }) => {
    const { number } = useSpring({
      from: { number: 0 },
      number: n,
      delay: 200,
      config: { mass: 1, tension: 20, friction: 10 },
    });
    return <animated.span>{number.to((n) => n.toFixed(0))}</animated.span>;
  };

  return (
    <ChakraProvider>
      <Box maxWidth="1200px" margin="auto" padding={6}>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <VStack spacing={6} align="stretch">
              <Heading as="h1" size="xl" textAlign="center">Advanced Indian Legal Case Comparison Dashboard</Heading>
              
              <Box
                borderWidth={1}
                borderRadius="lg"
                p={4}
                bg={bgColor}
                borderColor={borderColor}
              >
                <HStack justifyContent="space-between" alignItems="center" mb={2}>
                  <Heading as="h2" size="md">Case Input</Heading>
                  <IconButton
                    icon={isInputExpanded ? <ChevronUp /> : <ChevronDown />}
                    onClick={() => setIsInputExpanded(!isInputExpanded)}
                    aria-label={isInputExpanded ? "Collapse input form" : "Expand input form"}
                  />
                </HStack>
                <Collapse in={isInputExpanded} animateOpacity>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    <GridItem colSpan={2}>
                      <Input
                        placeholder="Case Title"
                        name="title"
                        value={currentCase.title}
                        onChange={handleInputChange}
                      />
                    </GridItem>
                    <Input
                      type="date"
                      name="date"
                      value={currentCase.date}
                      onChange={handleInputChange}
                    />
                    <Input
                      placeholder="Charges (e.g., Section 302 IPC)"
                      name="charges"
                      value={currentCase.charges}
                      onChange={handleInputChange}
                    />
                    <GridItem colSpan={2}>
                      <Textarea
                        placeholder="Case Description"
                        name="description"
                        value={currentCase.description}
                        onChange={handleInputChange}
                        rows={4}
                      />
                    </GridItem>
                    <Input
                      placeholder="Verdict (if applicable)"
                      name="verdict"
                      value={currentCase.verdict}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Collapse>
              </Box>

              <Box
                borderWidth={1}
                borderRadius="lg"
                p={4}
                bg={bgColor}
                borderColor={borderColor}
              >
                <Heading as="h3" size="sm" mb={2}>Filters and Options</Heading>
                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <Input
                    placeholder="Filter by charges"
                    name="charges"
                    value={filterCriteria.charges}
                    onChange={handleFilterChange}
                  />
                  <Select
                    name="minSimilarity"
                    value={filterCriteria.minSimilarity}
                    onChange={handleFilterChange}
                  >
                    <option value={0}>All Similarities</option>
                    <option value={50}>50% or higher</option>
                    <option value={75}>75% or higher</option>
                    <option value={90}>90% or higher</option>
                  </Select>
                  <Checkbox
                    name="onlyHighImpact"
                    isChecked={filterCriteria.onlyHighImpact}
                    onChange={handleFilterChange}
                  >
                    Only High Impact Cases
                  </Checkbox>
                </Grid>
                <Flex justify="space-between" mt={4}>
                  <Button
                    leftIcon={<Database />}
                    colorScheme={isUsingLocalData ? "green" : "gray"}
                    onClick={() => setIsUsingLocalData(!isUsingLocalData)}
                  >
                    {isUsingLocalData ? "Using Local Data" : "Use Local Data"}
                  </Button>
                  <Button
                    leftIcon={<Search />}
                    colorScheme="blue"
                    onClick={fetchSimilarCases}
                    isLoading={loading}
                    loadingText="Analyzing..."
                  >
                    Find Similar Cases
                  </Button>
                </Flex>
              </Box>

              {error && (
                <Box p={4} bg="red.100" color="red.700" borderRadius="md">
                  <HStack>
                    <AlertTriangle />
                    <Text>{error}</Text>
                  </HStack>
                </Box>
              )}

              {similarCases.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Heading as="h2" size="lg" mb={3}>Similar Cases</Heading>
                  <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={4}>
                    {similarCases.map((c, index) => (
                      <GridItem key={index}>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Box
                            p={4}
                            borderWidth={1}
                            borderRadius="md"
                            cursor="pointer"
                            onClick={() => {
                              setSelectedCase(c);
                              onOpen();
                            }}
                            _hover={{ bg: "gray.50" }}
                            transition="background-color 0.2s"
                          >
                            <Heading as="h3" size="md">{c.title}</Heading>
                            <Text><strong>Date:</strong> {c.date}</Text>
                            <Text><strong>Charges:</strong> {c.charges}</Text>
                            <Text><strong>Verdict:</strong> {c.verdict}</Text>
                            <HStack justify="space-between" mt={2}>
                              <Badge colorScheme="green">
                                Similarity: <AnimatedNumber n={c.similarityScore} />%
                              </Badge>
                              <Badge colorScheme="purple">
                                Impact: <AnimatedNumber n={c.legalImpact} />%
                              </Badge>
                            </HStack>
                          </Box>
                        </motion.div>
                      </GridItem>
                    ))}
                  </Grid>
                </motion.div>
              )}

              {selectedCase && (
                <Modal isOpen={isOpen} onClose={onClose} size="xl">
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader>{selectedCase.title}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <Text><strong>Date:</strong> {selectedCase.date}</Text>
                      <Text><strong>Charges:</strong> {selectedCase.charges}</Text>
                      <Text><strong>Description:</strong> {selectedCase.description}</Text>
                      <Text><strong>Verdict:</strong> {selectedCase.verdict}</Text>
                      <Text><strong>Similarities:</strong> {selectedCase.similarities}</Text>
                      <Text><strong>Differences:</strong> {selectedCase.differences}</Text>
                      <HStack spacing={4} mt={4}>
                        <VStack>
                          <ChakraTooltip label="Similarity to current case">
                            <CircularProgress value={selectedCase.similarityScore} color="green.400" size="120px">
                              <CircularProgressLabel>{selectedCase.similarityScore}%</CircularProgressLabel>
                            </CircularProgress>
                          </ChakraTooltip>
                          <Text>Similarity</Text>
                        </VStack>
                        <VStack>
                          <ChakraTooltip label="Value as a legal precedent">
                            <CircularProgress value={selectedCase.precedentValue} color="blue.400" size="120px">
                              <CircularProgressLabel>{selectedCase.precedentValue}%</CircularProgressLabel>
                            </CircularProgress>
                          </ChakraTooltip>
                          <Text>Precedent Value</Text>
                        </VStack>
                        <VStack>
                          <ChakraTooltip label="Impact on legal landscape">
                            <CircularProgress value={selectedCase.legalImpact} color="purple.400" size="120px">
                              <CircularProgressLabel>{selectedCase.legalImpact}%</CircularProgressLabel>
                            </CircularProgress>
                          </ChakraTooltip>
                          <Text>Legal Impact</Text>
                        </VStack>
                        <VStack>
                          <ChakraTooltip label="Level of public interest">
                            <CircularProgress value={selectedCase.publicInterest} color="orange.400" size="120px">
                              <CircularProgressLabel>{selectedCase.publicInterest}%</CircularProgressLabel>
                            </CircularProgress>
                          </ChakraTooltip>
                          <Text>Public Interest</Text>
                        </VStack>
                      </HStack>
                    </ModalBody>
                    <ModalFooter>
                      <Button colorScheme="blue" mr={3} onClick={onClose}>
                        Close
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              )}

              {analysis && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <Box
                    p={6}
                    borderWidth={1}
                    borderRadius="lg"
                    boxShadow="lg"
                    bg={bgColor}
                    borderColor={borderColor}
                  >
                    <Heading as="h2" size="lg" mb={3}>Analysis</Heading>
                    <Text whiteSpace="pre-line">{analysis}</Text>
                  </Box>
                </motion.div>
              )}

              {similarCases.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <Tabs isFitted variant="enclosed" colorScheme="blue">
                    <TabList mb="1em">
                      <Tab><BarChart2 />&nbsp;Timeline</Tab>
                      <Tab><PieChart />&nbsp;Similarity</Tab>
                      <Tab><Activity />&nbsp;Multi-factor</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel>
                        <Box
                          p={6}
                          borderWidth={1}
                          borderRadius="lg"
                          boxShadow="lg"
                          bg={bgColor}
                          borderColor={borderColor}
                        >
                          <Heading as="h3" size="md" mb={3}>Case Comparison Timeline</Heading>
                          <ResponsiveContainer width="100%" height={400}>
                            <LineChart data={caseTimeline}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis dataKey="date" />
                              <Tooltip />
                              <Legend />
                              <Line type="monotone" dataKey="date" stroke="#8884d8" activeDot={{ r: 8 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                      </TabPanel>
                      <TabPanel>
                        <Box
                          p={6}
                          borderWidth={1}
                          borderRadius="lg"
                          boxShadow="lg"
                          bg={bgColor}
                          borderColor={borderColor}
                        >
                          <Heading as="h3" size="md" mb={3}>Case Similarity Comparison</Heading>
                          <HStack spacing={8} align="start">
                            <Box width="50%">
                              <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={caseSimilarityData}>
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis />
                                  <Tooltip />
                                  <Legend />
                                  <Bar dataKey="similarity" fill="#8884d8" />
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                            <Box width="50%">
                              <Pie data={pieChartData} />
                            </Box>
                          </HStack>
                        </Box>
                      </TabPanel>
                      <TabPanel>
                        <Box
                          p={6}
                          borderWidth={1}
                          borderRadius="lg"
                          boxShadow="lg"
                          bg={bgColor}
                          borderColor={borderColor}
                        >
                          <Heading as="h3" size="md" mb={3}>Multi-factor Case Analysis</Heading>
                          <ResponsiveContainer width="100%" height={400}>
                            <RadarChart outerRadius={150} data={radarData}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="subject" />
                              <PolarRadiusAxis angle={30} domain={[0, 100]} />
                              <Radar name="Similarity" dataKey="Similarity" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                              <Radar name="Precedent Value" dataKey="Precedent Value" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                              <Radar name="Legal Impact" dataKey="Legal Impact" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} />
                              <Radar name="Public Interest" dataKey="Public Interest" stroke="#ff7300" fill="#ff7300" fillOpacity={0.6} />
                              <Legend />
                            </RadarChart>
                          </ResponsiveContainer>
                        </Box>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </motion.div>
              )}

              <HStack justify="space-between">
                <Button
                  leftIcon={<Download />}
                  colorScheme="green"
                  onClick={generatePDF}
                  isDisabled={similarCases.length === 0}
                >
                  Generate Report
                </Button>
                <Button
                  leftIcon={<Info />}
                  colorScheme="purple"
                  onClick={() => {
                    toast({
                      title: "About This Tool",
                      description: "This advanced case comparison tool uses AI and a comprehensive database to analyze and compare Indian legal cases, providing insights on similarity, precedent value, legal impact, and public interest.",
                      status: "info",
                      duration: 5000,
                      isClosable: true,
                    });
                  }}
                >
                  About This Tool
                </Button>
              </HStack>
              </VStack>
          </motion.div>
        </AnimatePresence>
      </Box>
    </ChakraProvider>
  );
};

export default CaseComparisonComponent;
